import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { motion } from "framer-motion";
import {
  Search,
  ArrowRight,
  ArrowLeft,
  Loader2,
  MapPin,
  Handshake,
  ExternalLink,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MLODirectory() {
  const [mlos, setMlos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Loan Officer Directory · Great Rate Check";
    (async () => {
      try {
        const res = await axios.get(`${API}/mlos/public`);
        setMlos(res.data || []);
      } catch {
        setMlos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mlos;
    return mlos.filter((m) => {
      const hay = [m.name, m.title, m.city_state, m.state, m.nmls, m.tagline, m.bio]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [mlos, query]);

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="mlo-directory-page">
      {/* Hero */}
      <header
        className="text-white"
        style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
          <Link to="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 w-auto" />
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link
              to="/partnerapply"
              data-testid="mlo-directory-back"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-white/90 transition-colors"
            >
              <ArrowLeft size={14} /> Back to the application
            </Link>
          </nav>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-14 pt-6 sm:pt-8">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">The Great Rate Check Network</p>
          <h1 className="mt-4 font-heading font-medium text-4xl sm:text-5xl lg:text-6xl text-balance max-w-3xl">
            Our loan officers. <span className="italic text-lime">Pick who fits your market.</span>
          </h1>
          <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-2xl">
            Every officer below is licensed at NEXA Mortgage LLC and vetted to run the Great Rate Check playbook. Browse by name or location, then check their rate page or apply to their partner program.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md">
            <div className="relative">
              <Search className="w-4 h-4 text-white/60 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, city, state, or NMLS…"
                data-testid="mlo-directory-search"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:bg-white/15 focus:border-lime focus:outline-none focus:ring-2 focus:ring-lime/40 transition"
              />
            </div>
            {!loading && (
              <p className="mt-2 text-xs text-white/60">
                {query
                  ? `Showing ${filtered.length} of ${mlos.length}`
                  : `${mlos.length} loan officer${mlos.length === 1 ? "" : "s"} in the network`}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        {loading ? (
          <div className="py-24 flex items-center justify-center text-ink-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading network…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState hasQuery={!!query} onClear={() => setQuery("")} />
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5"
            data-testid="mlo-directory-grid"
          >
            {filtered.map((m, i) => (
              <MLOCard key={m.slug} m={m} index={i} />
            ))}
          </div>
        )}
      </main>

      {/* CTA band — for BDMs / agents */}
      <section
        className="text-white"
        style={{ background: "linear-gradient(135deg, #17385e 0%, #1F8F8B 100%)" }}
        data-testid="mlo-directory-cta-band"
      >
        <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 border border-white/20 mb-4">
            <Handshake className="w-7 h-7 text-lime" />
          </div>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl text-balance">
            Real estate professional?
          </h2>
          <p className="mt-4 text-lg text-white/85 max-w-2xl mx-auto">
            The Great Rate Check Partner Program pairs top-producing agents with a dedicated loan officer for co-marketing, monthly asset packets, and long-term deal flow. Applying under a specific officer above ties you to them directly.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/partnerprogram"
              data-testid="mlo-directory-partnerprogram"
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
            >
              Learn about the Partner Program <ArrowRight size={18} />
            </Link>
            <Link
              to="/partnerapply"
              data-testid="mlo-directory-partnerapply"
              className="text-white/85 hover:text-white text-sm font-medium underline decoration-white/30 underline-offset-4"
            >
              Skip ahead — apply now
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-ink-500">
        Great Rate Check · NEXA Mortgage LLC · Equal Housing Lender. Licensed loan officers only. NMLS numbers displayed above.
      </footer>
    </div>
  );
}

function EmptyState({ hasQuery, onClear }) {
  return (
    <div className="rounded-3xl border border-dashed border-linen-border bg-white/60 py-16 text-center max-w-lg mx-auto">
      <Search className="w-8 h-8 text-ink-500 mx-auto mb-3" />
      <p className="text-navy font-medium">
        {hasQuery ? "No officers match that search." : "Network coming soon."}
      </p>
      {hasQuery && (
        <button
          onClick={onClear}
          data-testid="mlo-directory-clear-search"
          className="mt-3 text-sm text-teal hover:underline"
        >
          Clear search
        </button>
      )}
    </div>
  );
}

function MLOCard({ m, index }) {
  const hasPortrait = m.has_photo || m.slug === "jason";
  const photoUrl = m.has_photo ? mloPhotoUrl(m) : m.slug === "jason" ? ASSETS.headshotApply : null;
  const location = m.city_state || m.state || "";

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
      data-testid={`mlo-directory-card-${m.slug}`}
      className="group rounded-2xl bg-white border border-linen-border overflow-hidden shadow-soft hover:shadow-lift transition-all hover:-translate-y-0.5 flex flex-col"
    >
      <Link
        to={`/${m.slug}`}
        className="block aspect-square w-full overflow-hidden bg-navy-deep relative"
        data-testid={`mlo-directory-card-portrait-${m.slug}`}
      >
        {hasPortrait && photoUrl ? (
          <img
            src={photoUrl}
            alt={m.name}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-deep via-[#0F2A47] to-[#17385e] p-6">
            <img
              src={ASSETS.logoMark}
              alt="Great Rate Check"
              className="w-full h-full object-contain opacity-80"
              loading="lazy"
            />
          </div>
        )}
      </Link>

      <div className="p-3.5 flex-1 flex flex-col">
        <p className="font-heading font-medium text-[15px] text-navy leading-tight truncate" title={m.name}>
          {m.name}
        </p>
        {location && (
          <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-500">
            <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{location}</span>
          </p>
        )}
        {m.nmls && <p className="text-[10px] text-ink-400 mt-0.5">NMLS #{m.nmls}</p>}

        <div className="mt-3 pt-3 border-t border-linen-border flex flex-col gap-1.5">
          <Link
            to={`/${m.slug}/partnerapply`}
            data-testid={`mlo-directory-card-apply-${m.slug}`}
            className="inline-flex items-center justify-center rounded-full bg-navy hover:bg-[#173a5f] text-white font-semibold text-xs px-3 py-2 transition-colors"
          >
            Apply with {m.name.split(" ")[0]}
          </Link>
          <Link
            to={`/${m.slug}`}
            data-testid={`mlo-directory-card-consumer-${m.slug}`}
            className="inline-flex items-center justify-center gap-1 text-teal hover:text-navy font-medium text-[11px] py-0.5 transition-colors"
          >
            <ExternalLink className="w-3 h-3" /> Their rate page
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
